import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './scss/createMatch.scss'; // Reuse the same styles
import NewLocation from './components/venues/NewLocation';
import SelectLocationModal from './components/SelectLocationModal';
import api from './services/apiService';
import { useNotification } from './components/NotificationProvider';

function CreateTrainingSession() {
  const [sessionDate, setSessionDate] = useState('');
  const [location, setLocation] = useState('');
  const [selectedPitchId, setSelectedPitchId] = useState(null);
  const [sessionTime, setSessionTime] = useState('19:30');
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [duration, setDuration] = useState(60); // Default for training
  const [pitches, setPitches] = useState([]);
  const [showNewLocation, setShowNewLocation] = useState(false);
  const [showSelectLocation, setShowSelectLocation] = useState(false);
  const [currentStage, setCurrentStage] = useState(1);
  const [instructions, setInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatEndDate, setRepeatEndDate] = useState('');
  const [isTBC, setIsTBC] = useState(false);
  const totalStages = 2; // Back to 2 stages with repeat settings in stage 1
  const navigate = useNavigate();
  const teamId = sessionStorage.getItem('selected_team_id');
  const teamName = sessionStorage.getItem('selected_team_name');
  const [dateError, setDateError] = useState('');
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await api.get(process.env.REACT_APP_API_URL + '/get_players.php?team_id=' + teamId);
        const result = response.data;
        if (result.success) {
          setPlayers(result.players);
          setSelectedPlayers(result.players.map(player => player.player_id));
        } else {
          console.error('Failed to fetch players');
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };

    const fetchSessions = async () => {
      try {
        const response = await api.get(process.env.REACT_APP_API_URL + '/get_training_sessions.php?team_id=' + teamId);
        const result = response.data;
        if (result.success) {
          setSessions(result.sessions);
        } else {
          console.error('Failed to fetch sessions');
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
      }
    };

    const fetchPitches = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_pitches.php`);
        const result = response.data;
        if (result.success) {
          setPitches(result.pitches);
        } else {
          console.error('Failed to fetch pitches:', result.message);
        }
      } catch (error) {
        console.error('Error fetching pitches:', error);
      }
    };

    fetchPlayers();
    fetchSessions();
    fetchPitches();
  }, []);

  const handlePlayerSelection = (playerId) => {
    setSelectedPlayers((prevSelected) =>
      prevSelected.includes(playerId)
        ? prevSelected.filter((id) => id !== playerId)
        : [...prevSelected, playerId]
    );
  };

  const handleLocationSelected = (pitch) => {
    const pitchId = typeof pitch.pitch_id === 'string' ? parseInt(pitch.pitch_id) : pitch.pitch_id;
    setSelectedPitchId(pitchId);
    setLocation(pitch.name);
    setShowSelectLocation(false);
  };

  const handleLocationAdded = async (newLocation) => {
    try {
      // The API call is already handled in NewLocation.js, so we just need to update the UI
      // Add the new pitch to the pitches array
      const newPitch = {
        pitch_id: newLocation.pitch_id,
        name: newLocation.name,
        address_1: newLocation.address_1,
        address_2: newLocation.address_2 || '',
        town: newLocation.town,
        county: newLocation.county,
        postcode: newLocation.postcode,
        description: newLocation.description || '',
        facilities: newLocation.facilities || '',
        organiser_contact: newLocation.organiser_contact || ''
      };
      
      setPitches(prevPitches => [...prevPitches, newPitch]);
      const pitchId = typeof newLocation.pitch_id === 'string' ? parseInt(newLocation.pitch_id) : newLocation.pitch_id;
      setSelectedPitchId(pitchId);
      setLocation(newLocation.name);
      setShowNewLocation(false);
    } catch (error) {
      console.error('Error handling location added:', error);
    }
  };

  const checkExistingSession = async (date) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/check_existing_match.php?team_id=${teamId}&date=${date}`
      );
      const result = response.data;
      
      if (result.success && result.exists) {
        setDateError(result.message);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking existing matches:', error);
      return false;
    }
  };


  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setSessionDate(newDate);
    
    if (newDate) {
      const hasExisting = await checkExistingSession(newDate);
      if (hasExisting) {
        // setDateError message is now handled in checkExistingMatch
        // based on the event type returned from the API
      } else {
        setDateError('');
      }
    }
  };

  const renderStageIndicator = () => (
    <div className="stage-indicator">
      {[...Array(totalStages)].map((_, index) => (
        <div 
          key={index} 
          className={`stage ${currentStage === index + 1 ? 'active' : ''} ${currentStage > index + 1 ? 'completed' : ''}`}
        >
          {index + 1}
        </div>
      ))}
    </div>
  );

  const renderStageContent = () => {
    switch (currentStage) {
      case 1:
        return (
          <>
            <h2>Session Details</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sessionDate">Date:</label>
                <input
                  type="date"
                  id="sessionDate"
                  value={sessionDate}
                  onChange={handleDateChange}
                  required
                  className={dateError ? 'error' : ''}
                />
                {dateError && <div className="error-message">{dateError}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sessionTime">Start Time:</label>
                <input
                  type="time"
                  id="sessionTime"
                  value={isTBC ? "00:00" : sessionTime}
                  onChange={(e) => setSessionTime(e.target.value)}
                  disabled={isTBC}
                  required={!isTBC}
                  style={{ opacity: isTBC ? 0.2 : 1 }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration">Duration (mins):</label>
                <input
                  type="number"
                  id="duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="1"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={isTBC}
                    onChange={(e) => {
                      setIsTBC(e.target.checked);
                      if (e.target.checked) {
                        setSessionTime("00:00");
                      }
                    }}
                  />
                  Start Time TBC (To Be Confirmed)
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={repeatWeekly}
                    onChange={(e) => setRepeatWeekly(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Repeat Weekly
                </label>
                <p className="checkbox-description">
                  This will create training sessions every week on the same day and time
                </p>
              </div>
            </div>
            
            {repeatWeekly && (
              <div className="form-group">
                <label htmlFor="repeatEndDate">End Date (Optional):</label>
                <input
                  type="date"
                  id="repeatEndDate"
                  value={repeatEndDate}
                  onChange={(e) => setRepeatEndDate(e.target.value)}
                  min={sessionDate}
                />
                <p className="field-description">
                  Leave empty to continue indefinitely.
                </p>
              </div>
            )}
          </>
        );

      case 2:
        return (
          <>
            <h2>Location & Instructions</h2>
            <div className="form-group location-group">
              <div className="label-with-button">
                <label htmlFor="location">Location:</label>
                <button 
                  type="button" 
                  className="add-location-button"
                  onClick={() => setShowNewLocation(true)}
                >
                  + Add New
                </button>
              </div>
              {pitches.length > 0 ? (
                <>
                  <button
                    type="button"
                    className="select-location-button"
                    onClick={() => setShowSelectLocation(true)}
                  >
                    {(() => {
                      if (!selectedPitchId) return '-- Select a Pitch --';
                      const selectedPitch = pitches.find(p => {
                        const pId = typeof p.pitch_id === 'string' ? parseInt(p.pitch_id) : p.pitch_id;
                        const sId = typeof selectedPitchId === 'string' ? parseInt(selectedPitchId) : selectedPitchId;
                        return pId === sId;
                      });
                      return selectedPitch?.name || '-- Select a Pitch --';
                    })()}
                  </button>
                  {selectedPitchId && (
                    <div className="selected-location-info">
                      {(() => {
                        const selectedPitch = pitches.find(p => {
                          const pId = typeof p.pitch_id === 'string' ? parseInt(p.pitch_id) : p.pitch_id;
                          const sId = typeof selectedPitchId === 'string' ? parseInt(selectedPitchId) : selectedPitchId;
                          return pId === sId;
                        });
                        if (!selectedPitch) {
                          return <span className="info-text">Location selected</span>;
                        }
                        return (
                          <>
                            {selectedPitch.town && selectedPitch.county && (
                              <span className="info-text">
                                {selectedPitch.town}, {selectedPitch.county}
                              </span>
                            )}
                            {selectedPitch.postcode && (
                              <span className="info-text">
                                {selectedPitch.postcode}
                              </span>
                            )}
                            {!selectedPitch.town && !selectedPitch.county && !selectedPitch.postcode && (
                              <span className="info-text">Location selected</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              ) : (
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Enter location"
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label htmlFor="instructions">Additional Instructions:</label>
              <textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Add any specific instructions for players..."
                rows="4"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (currentStage === 1) {
      const hasExisting = await checkExistingSession(sessionDate);
      if (hasExisting) {
        setDateError('A training session already exists on this date');
        return;
      }
    }
    
    if (currentStage < totalStages) {
      setCurrentStage(currentStage + 1);
    }
  };

  const handleBack = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStage < totalStages) {
      handleNext();
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/create_match.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          match_date: sessionDate,
          location: location,
          pitch_id: selectedPitchId,
          match_time: sessionTime,
          players: selectedPlayers,
          duration,
          team_id: teamId,
          team_name: teamName,
          instructions,
          opposition: null,
          type: 'training',
          team_type: 'training',
          team_a: null,
          team_b: null,
          is_home: null,
          max_players: 9999,
          // Repeat settings
          repeat_weekly: repeatWeekly,
          repeat_end_date: repeatEndDate || null,
          isTBC: isTBC
        }),
      });

      const result = response.data;
      if (result.success) {
        const successMessage = repeatWeekly 
          ? 'Repeat training session created successfully! Players have been notified.'
          : 'Training session created successfully! Players have been notified.';
        showSuccess(successMessage, 5000);
        navigate('/dashboard');
      } else {
        showError(`Failed to create training session: ${result.message || 'Unknown error'}`, 5000);
        console.error('Failed to create training session:', result.message);
      }
    } catch (error) {
      showError('An error occurred while creating the training session. Please try again.', 5000);
      console.error('Error creating training session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="CreateMatchForm container">
      <Header />
      <button className="back-button" onClick={() => window.history.back()}>← Back</button>
      <h1>New Training Session</h1>
      {renderStageIndicator()}
      <form onSubmit={handleSubmit}>
        {renderStageContent()}
        <div className="form-navigation">
          {currentStage > 1 && (
            <button 
              type="button" 
              onClick={handleBack} 
              className="back-stage-button"
              disabled={isLoading}
            >
              Previous
            </button>
          )}
          <button 
            type="submit" 
            className="next-stage-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-spinner">
                Creating session...
              </span>
            ) : (
              currentStage === totalStages ? 'Create Session' : 'Next'
            )}
          </button>
        </div>
      </form>

      {showNewLocation && (
        <NewLocation 
          onClose={() => setShowNewLocation(false)}
          onLocationAdded={handleLocationAdded}
        />
      )}

      {showSelectLocation && pitches.length > 0 && (
        <SelectLocationModal
          pitches={pitches}
          onClose={() => setShowSelectLocation(false)}
          onSelectLocation={handleLocationSelected}
        />
      )}
    </div>
  );
}

export default CreateTrainingSession; 